import { proxy } from 'valtio'
import type { PacketData } from '../ReplayPanel'
import { appQueryParams, updateQsParam } from '../../appParams'
import type { ReplayEvent } from '../../packetsReplay/extractReplayEvents'

export const packetsReplayState = proxy({
  packetsPlayback: [] as PacketData[],
  isOpen: false,
  isMinimized: false,
  isRecording: false,
  replayMinecraftVersion: '',
  replayName: '',
  isPlaying: false,
  showPacketList: false,
  // Mirrors TelemetryPanel's local collapsed flag so other HUD
  // components (e.g. TopMenu) can mirror the same animated state.
  telemetryCollapsed: false,
  // Mirrors InfoViewPanel's local collapsed flag so TopMenu can
  // follow InfoViewPanel's right edge when it slides off-screen.
  infoViewCollapsed: false,
  seekTo: null as number | null,
  internalRewindTo: null as number | null,
  // Seeking replays every packet up to the target; the UI hides the game behind a loader.
  isSeeking: false,
  replayEvents: [] as ReplayEvent[],
  totalTimeMs: 0,
  progress: {
    current: 0,
    total: 0
  },
  speed: appQueryParams.replaySpeed ? parseFloat(appQueryParams.replaySpeed) : 1,
  customButtons: {
    validateClientPackets: {
      state: appQueryParams.replayValidateClient === 'true',
      label: 'C',
      tooltip: 'Validate client packets'
    },
    stopOnError: {
      state: appQueryParams.replayStopOnError === 'true',
      label: 'E',
      tooltip: 'Stop the replay when an error occurs'
    },
    skipMissingOnTimeout: {
      state: appQueryParams.replaySkipMissingOnTimeout === 'true',
      label: 'T',
      tooltip: 'Skip missing packets on timeout'
    },
    packetsSenderDelay: {
      state: appQueryParams.replayPacketsSenderDelay === 'true',
      label: 'D',
      tooltip: 'Send packets with an additional delay'
    }
  }
})

/* ── Индекс play-пакета ↔ время ──────────────────────────────────────────────
   `progress.current` — это порядковый номер пакета, а маркеры событий стоят по
   реальному `packet.time`. Пересчёт «индекс → время» линейно (index/total *
   totalTimeMs) верен только при равномерной плотности пакетов; на реальных
   записях он врёт на секунды, и события выглядят запаздывающими относительно
   плейхеда. Поэтому держим реальные времена play-пакетов.
   Вне valtio: десятки тысяч чисел не нужно проксировать и снапшотить. */
export const replayTimeIndex = { times: [] as number[] }

/** Время play-пакета по его индексу; `null`, если таблицы ещё нет. */
export const packetTimeAt = (index: number): number | null => {
  const { times } = replayTimeIndex
  if (!times.length) return null
  return times[Math.max(0, Math.min(times.length - 1, index))]!
}

/** Индекс первого play-пакета не раньше `timeMs`; `null`, если таблицы ещё нет. */
export const indexAtTime = (timeMs: number): number | null => {
  const { times } = replayTimeIndex
  if (!times.length) return null
  let lo = 0
  let hi = times.length - 1
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (times[mid]! < timeMs) lo = mid + 1
    else hi = mid
  }
  return lo
}

export const onChangeButtonState = (button: keyof typeof packetsReplayState.customButtons, state: boolean) => {
  packetsReplayState.customButtons[button].state = state
  switch (button) {
    case 'validateClientPackets': {
      updateQsParam('replayValidateClient', String(state))
      break
    }
    case 'stopOnError': {
      updateQsParam('replayStopOnError', String(state))
      break
    }
    case 'skipMissingOnTimeout': {
      updateQsParam('replaySkipMissingOnTimeout', String(state))
      break
    }
    case 'packetsSenderDelay': {
      updateQsParam('replayPacketsSenderDelay', String(state))
      break
    }
  }
}
